<template>
	<div class="shared-botpress">
		<o-button
			id="bp-toggle-chat"
			class="shared-botpress__trigger"
			color="default"
			size="md">
			<svg
				class="shared-botpress__icon"
				viewBox="0 0 14.3797 14.3797"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				aria-hidden="true">
				<path
					d="M6.25532 0.644144C6.58236 -0.214716 7.79737 -0.214714 8.12441 0.644147L9.51222 4.28882C9.61374 4.55543 9.8243 4.76599 10.0909 4.86751L13.7356 6.25532C14.5944 6.58236 14.5944 7.79737 13.7356 8.12441L10.0909 9.51222C9.8243 9.61374 9.61374 9.8243 9.51222 10.0909L8.12441 13.7356C7.79737 14.5944 6.58236 14.5944 6.25532 13.7356L4.8675 10.0909C4.76599 9.8243 4.55543 9.61374 4.28882 9.51222L0.644144 8.12441C-0.214716 7.79737 -0.214714 6.58236 0.644147 6.25532L4.28882 4.8675C4.55543 4.76599 4.76599 4.55543 4.86751 4.28882L6.25532 0.644144Z"
					fill="url(#bp-assistant-star)"/>
				<defs>
					<linearGradient
						id="bp-assistant-star"
						x1="15.8782"
						y1="11.6899"
						x2="-0.647442"
						y2="11.6982"
						gradientUnits="userSpaceOnUse">
						<stop stop-color="#F9E271"/>
						<stop
							offset="0.504791"
							stop-color="#49E1B3"/>
						<stop
							offset="1"
							stop-color="#30B2F0"/>
					</linearGradient>
				</defs>
			</svg>
			<span class="shared-botpress__label">{{ $t('common.shared.botpress.trigger') }}</span>
		</o-button>
	</div>
</template>

<script setup lang="ts">
// #region Setup
import type { SharedBotpressEmits, SharedBotpressProps } from './SharedBotpressSetup';
import SharedBotpressSetup from './SharedBotpressSetup';
const emits = defineEmits<SharedBotpressEmits>() as SharedBotpressEmits;
const props = withDefaults(defineProps<SharedBotpressProps>(), SharedBotpressSetup.defaultProps);
const setup = new SharedBotpressSetup(props, emits);
// #endregion
</script>

<style scoped lang="less">
@import "@/styles/variables.less";

.shared-botpress {
	position: fixed;
	// z-index 2 comme la bulle de discussion, pour passer au-dessus du contenu
	// (#arm-main est un contexte d'empilement en z-index 1)
	z-index: 2;
	// Juste à gauche de la bulle de discussion (elle : right 1.5rem, largeur 3rem)
	right: 5.5rem;
	// 1.75rem (et non 1.5) : centre le bouton (40px) au même niveau que la
	// bulle de discussion (48px), qui est calée un peu plus bas
	bottom: 1.75rem;
	transition: right 0.3s ease-out, bottom 0.3s ease-out;

	// Barre d'actions de sélection visible : on remonte comme la bulle.
	// Sur mobile la barre est haute (actions empilées) : on masque le bouton,
	// comme le fait la bulle de discussion, pour ne pas se superposer à elle.
	body.orion-body--footer-fixed-visible & {
		// 7.75rem pour rester centré avec la bulle (même décalage qu'au repos)
		bottom: 7.75rem;

		@media @layoutNavBottom {
			display: none;
		}
	}

	// Panneau latéral droit ouvert : la bulle passe à right 20.5rem, on suit
	#app > #right-aside:not(:empty) ~ & {
		right: 24.5rem;
	}

	&__trigger {
		// Bordure fine du visuel Figma (le bouton Orion default n'en a pas)
		border-color: var(--grey-light);

		// Survol : la bordure se renforce (comme les boutons Orion) + légère ombre
		&:hover,
		&:focus-visible {
			border-color: var(--grey);
			box-shadow: 0 0.4rem 1rem -0.3rem rgb(0 0 0 / 18%);
		}

		// Visuel Figma : "Assistant" en casse normale + icône à gauche
		:deep(.orion-button__text) {
			display: inline-flex;
			gap: 0.5rem;
			align-items: center;
			text-transform: none;
		}
	}

	&__icon {
		flex: none;
		width: 1.125rem;
		height: 1.125rem;
	}

	@media @layoutNavBottom {
		bottom: 6.25rem;
	}

	// Menu en bas (mobile) : la bulle remonte à 5rem, on s'aligne
	@media @phone {
		right: 5rem;
		bottom: 4.75rem;
	}
}

// Discussion ouverte (liste ou fenêtres de chat) : elle occupe le même coin,
// on masque le bouton pour qu'il ne reste pas au premier plan par-dessus.
:global(.shared-discussions:has(.shared-discussions__opened) ~ .shared-botpress) {
	display: none;
}
</style>
